import {
  Text,
  Heading,
  VStack,
  Alert,
  CloseButton,
  AlertDescription,
} from "@chakra-ui/react";
import { ProblemCode } from "@syla/shared/errors/ProblemCode";
import {
  isSylaApiErrorCode,
  SylaApiError,
} from "@syla/shared/errors/SylaApiError";
import React from "react";
import { errorToToastOptions } from "../../../../api/errors/errorToToastOptions";
import { MarkdocContent } from "../../../../markdoc/MarkdocContent";
import { ImportAssistButton } from "../../../atoms/ImportAssistButton";
import { WindowOpenFailedError } from "../helpers/openOAuthWindow";

/**
 * Display a closable error message due to a failed import that was just attempted.
 *
 * Handles file imports, Oauth errors and API sync errors.
 */
export function ImportErrorAlert({
  error,
  isCustomDataSource,
  onClose,
}: {
  error: SylaApiError | unknown | undefined;
  isCustomDataSource: boolean;
  onClose?: () => void;
}): React.ReactElement | null {
  const content = errorBoxContent(error, isCustomDataSource);

  if (!content) {
    return null;
  }

  return (
    // overflow defaults to hidden on Alert, so we need to override back to
    // visible, otherwise the whole alert can get squished to 0 height.
    <Alert
      overflow="visible"
      status="error"
      borderRadius="md"
      variant="subtle"
      alignItems="flex-start"
    >
      <AlertDescription w="full">
        <VStack spacing={3} py={1} alignItems="flex-start" w="full">
          {content}
        </VStack>
      </AlertDescription>
      {onClose && (
        <CloseButton position="absolute" right={2} top={2} onClick={onClose} />
      )}
    </Alert>
  );
}

function errorBoxContent(
  error: unknown,
  isCustomDataSource: boolean,
): React.ReactElement | null {
  if (!error) {
    return null;
  }

  // handle failures during file imports
  if (
    isSylaApiErrorCode(error, ProblemCode.UnsupportedFileType) ||
    isSylaApiErrorCode(error, ProblemCode.FailedFileImport) ||
    isSylaApiErrorCode(error, ProblemCode.InvalidCustomFileTemplate)
  ) {
    const isCustomFileImport = isSylaApiErrorCode(
      error,
      ProblemCode.InvalidCustomFileTemplate,
    );

    const heading = (
      <Heading size="sm">{"Your file couldn't be imported"}</Heading>
    );

    const subHeading =
      isCustomFileImport && error.data.userMessage ?
        <MarkdocContent content={error.data.userMessage} />
      : <Text>
          {
            "Syla couldn’t import the transactions in your file. This usually means the file format isn’t supported or some transaction types aren’t recognised."
          }
        </Text>;

    const advice =
      isCustomDataSource || isCustomFileImport ?
        <MarkdocContent
          content={
            "Please read the [Help Guide](https://help.syla.com.au/en/articles/8281571-custom-csv-template-import-advanced) and double check your file."
          }
        />
      : <Text>
          {"Please check you're importing the original file and try again."}
        </Text>;

    const footer = (
      <Text>{"If it still doesn’t work, click below to request help."}</Text>
    );

    return (
      <>
        {heading}
        {subHeading}
        {advice}
        {footer}
        <ImportAssistButton />
      </>
    );
  }

  // handle blocked window during oauth flow
  else if (error instanceof WindowOpenFailedError) {
    return (
      <>
        <Heading size="sm">Window Blocked</Heading>
        <Text>
          The Connect window was blocked from opening. Please disable your
          ad-blocker and try again.
        </Text>
      </>
    );
  } else {
    const props = errorToToastOptions(error);

    return (
      <>
        {props.title && <Heading size="sm">{props.title}</Heading>}
        <Text>{props.description}</Text>
      </>
    );
  }
}
